import * as React from 'react';
import { Drawer, Toolbar, IconButton, CssBaseline, Box, Typography, useTheme, Button, Skeleton, AppBar } from '@mui/material';
import { Menu as MenuIcon, Close } from '@mui/icons-material';
import ProfileMenu from '../ProfileMenu';
import CustomAutocomplete from '../CustomAutocomplete';
import NestedList from '../NestedList';
import { fixedDigitsFormat } from '../../../utils/functions';
import { HorizontalMenuProps } from '..';
import { DrawerHeader, drawerWidth } from './utils';
import { colors } from '../../../utils/palette';
import { useEffect } from 'react';
import useGetCurrency from '../../../hooks/useGetCurrency';

export interface DefaultSelectedItem {
  main: string;
  child?: string;
}

const HorizontalMobileMenu: React.FC<HorizontalMenuProps> = ({
  profileMenu,
  balance,
  icon,
  activeCompany,
  defaultSelectedMenu,
  companies,
  setItem,
  submenus,
  isLoadingCompanies,
  isLoadingBalance,
  isUserAdmin,
  returnToHomeFn,
}) => {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [selectedMenu, setSelectedMenu] = React.useState<DefaultSelectedItem>(defaultSelectedMenu);
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (defaultSelectedMenu !== selectedMenu) setSelectedMenu(defaultSelectedMenu);
  }, [defaultSelectedMenu]);

  if (!isUserAdmin) {
    // filter out admin only content
    submenus = submenus.filter((submenu) => !submenu.isAdminOnlyContent);
  }

  return (
    <Box component="div" style={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar>
        <Toolbar
          sx={{
            justifyContent: open ? 'end' : 'space-between',
            backgroundColor: theme.palette.text.primary,
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={[
              {
                mr: 2,
              },
              open && { display: 'none' },
            ]}
          >
            <MenuIcon sx={{ color: colors.almostWhite }} />
          </IconButton>
          <Box component="div" style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) }}>
            <Box component="div" style={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
              <Typography
                variant="body2"
                style={{
                  fontWeight: 600,
                  color: colors.almostWhite,
                }}
              >
                Balance:
              </Typography>
              {isLoadingBalance ? (
                <Skeleton width={110} />
              ) : (
                <Typography variant="body2" sx={{ color: colors.almostWhite }}>{`${useGetCurrency()} ${fixedDigitsFormat(balance, 2)}`}</Typography>
              )}
            </Box>
            <ProfileMenu {...profileMenu} />
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          position: 'absolute',
          '& .MuiDrawer-paper': {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: theme.palette.background.default,
          },
        }}
        onClose={handleDrawerClose}
        open={open}
      >
        <DrawerHeader
          sx={{
            justifyContent: 'space-between',
            marginBottom: theme.spacing(1.25),
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
          }}
        >
          <Button disableRipple sx={{ p: 0, '&:hover': { backgroundColor: 'transparent' } }} variant="text" onClick={returnToHomeFn} data-testid="icon-img">
            {icon}
          </Button>
          <IconButton onClick={handleDrawerClose} sx={{ marginRight: theme.spacing(-1) }} data-testid="close-drawer">
            {<Close sx={{ fill: theme.palette.text.secondary }} />}
          </IconButton>
        </DrawerHeader>

        {isUserAdmin && (
          <CustomAutocomplete items={companies} activeItem={activeCompany} setItem={setItem} textFieldTitle="Active Company" isLoading={isLoadingCompanies} />
        )}
        <Box component="div" sx={{ mt: 4.5 }}>
          <NestedList nestedList={submenus} setSelectedMenu={setSelectedMenu} selectedMenu={selectedMenu} closeFunction={handleDrawerClose} />
        </Box>
      </Drawer>

      <DrawerHeader />
    </Box>
  );
};

export default HorizontalMobileMenu;
